import type { DefaultNamespace } from 'i18next'
import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import { cn } from '@/helpers/utils'

export interface TextAreaProps
	extends TextareaHTMLAttributes<HTMLTextAreaElement> {
	className?: string
	label?: string | DefaultNamespace
	name?: string
	typeIcon?: ReactNode
	touched?: boolean
	errorMessage?: string | DefaultNamespace
	helpText?: string | DefaultNamespace
	placeholder?: string
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	(
		{
			label,
			name,
			typeIcon,
			touched = false,
			errorMessage,
			helpText,
			className,
			disabled,
			placeholder,
			...rest
		},
		ref
	) => {
		return (
			<div
				className={cn(
					'flex min-h-[92px] w-full flex-col gap-1.5',
					className
				)}
			>
				{label && <Label htmlFor={name}>{label}</Label>}
				<div className='flex flex-row items-center'>
					<div
						className={cn(
							'relative flex w-full cursor-pointer items-center justify-center gap-1 rounded-lg border border-neutral-300 bg-neutral-white p-3 transition-all duration-200',
							errorMessage && 'border-danger-300',
							disabled &&
								'cursor-not-allowed bg-neutral-100 text-neutral-500',
							!disabled &&
								'hover:border-neutral-300 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),0_0_0_4px_var(--color-neutral-100)]',
							!disabled &&
								errorMessage &&
								'hover:border-danger-300 hover:shadow-[0_1px_2px_0_rgba(16,24,40,0.05),0_0_0_4px_var(--color-danger-100)]'
						)}
					>
						{typeIcon}
						<textarea
							className='h-full w-full flex-1 resize-none border-none text-base text-neutral-900 outline-none placeholder:text-neutral-400 focus:outline-none disabled:cursor-not-allowed'
							disabled={disabled}
							name={name}
							placeholder={placeholder}
							ref={ref}
							{...rest}
						/>
					</div>
				</div>
				<InputHelpText error={errorMessage} helpText={helpText} />
			</div>
		)
	}
)

TextArea.displayName = 'TextArea'
